import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Avatar,
  IconButton,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  bannerAPI,
  categoryAPI,
  productAPI,
  groupsAPI,
} from "../../services/api";

interface BannerMedia {
  image: string;
  video: string | null;
}

interface BannerLink {
  product: any;
  category: any;
  link: string | null;
  productId: string | null;
  productname: string | null;
  categoryId: string | null;
  categoryname: string | null;
}

interface Banner {
  id: number;
  media: BannerMedia[];
  link: BannerLink[];
  groupId?: string; // <-- Add this line
}

interface Category {
  id: string;
  name: string;
  categorystatus: number;
  totalproducts: number;
  totalsubcategories: number;
  createdby: string;
  modifiedby: string | null;
  parentId: string | null;
  deletedAt: string | null;
  created: string;
  modified: string;
  groupcategoryId: string | null;
  masterdetailId: string;
}

interface Product {
  id: string;
  name: string;
  productstatus: number;
  price: number;
  availablequantity: number;
  categoryId: string;
  category?: {
    id: string;
    name: string;
  };
}

interface BannerDialogProps {
  open: boolean;
  onClose: () => void;
  banner?: Banner | null;
  onSave: (banner: any) => void;
  mode: "add" | "edit" | "view";
}

const BannerDialog: React.FC<BannerDialogProps> = ({
  open,
  onClose,
  banner,
  onSave,
  mode,
}) => {
  // Remove unused dispatch and categories
  const [loading, setLoading] = useState(false);
  // const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>("");
  const [mediaLoading, setMediaLoading] = useState(false);
  const [linkType, setLinkType] = useState<"product" | "category" | "custom">(
    "product"
  );
  const [productId, setProductId] = useState("");
  const [productName, setProductName] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [categoryName, setCategoryName] = useState("");
  const [customLink, setCustomLink] = useState("");
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [productLoading, setProductLoading] = useState(false);
  const [groupLoading, setGroupLoading] = useState(false);
  const [activeCategories, setActiveCategories] = useState<Category[]>([]);
  const [activeProducts, setActiveProducts] = useState<Product[]>([]);
  const [activeGroups, setActiveGroups] = useState<
    { id: string; name: string }[]
  >([]);
  const [groupId, setGroupId] = useState("");

  // Validation errors
  const [errors, setErrors] = useState<{
    image: string;
    productId: string;
    categoryId: string;
    categoryName: string;
    customLink: string;
    groupId?: string;
  }>({
    image: "",
    productId: "",
    categoryId: "",
    categoryName: "",
    customLink: "",
    groupId: "",
  });

  // Load active categories, products, and groups on component mount
  useEffect(() => {
    loadActiveCategories();
    loadActiveProducts();
    loadActiveGroups();
  }, []);

  const loadActiveCategories = async () => {
    try {
      setCategoryLoading(true);
      const response = await categoryAPI.getActiveCategories();

      // Handle nested response structure: response.data.data
      let categories = [];
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        categories = response.data.data;
      } else if (Array.isArray(response.data)) {
        categories = response.data;
      }

      setActiveCategories(categories);
    } catch (error) {
      setActiveCategories([]); // Set empty array on error
    } finally {
      setCategoryLoading(false);
    }
  };

  const loadActiveProducts = async () => {
    try {
      setProductLoading(true);
      const response = await productAPI.getActiveProducts();

      // Handle nested response structure: response.data.data
      let products = [];
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        products = response.data.data;
      } else if (Array.isArray(response.data)) {
        products = response.data;
      }

      setActiveProducts(products);
    } catch (error) {
      setActiveProducts([]); // Set empty array on error
    } finally {
      setProductLoading(false);
    }
  };

  const loadActiveGroups = async () => {
    try {
      setGroupLoading(true);
      const response = await groupsAPI.getActiveGroups();
      let groups = [];
      if (
        response.data &&
        response.data.data &&
        Array.isArray(response.data.data)
      ) {
        groups = response.data.data;
      } else if (Array.isArray(response.data)) {
        groups = response.data;
      }
      setActiveGroups(groups);
    } catch (error) {
      setActiveGroups([]);
    } finally {
      setGroupLoading(false);
    }
  };

  useEffect(() => {
    if (banner && banner.media && banner.media.length > 0) {
      const imageUrl = categoryAPI.getMediaDownloadUrl(banner.media[0].image);
      setPreviewUrl(imageUrl);
    } else {
      setPreviewUrl("");
    }
    setSelectedFile(null);
  }, [banner]);

  // Auto-fill form data when editing
  useEffect(() => {
    if (banner && mode === "edit") {
      // Auto-fill link configuration based on existing data
      if (banner.link && banner.link.length > 0) {
        const link = banner.link[0];

        if (link.productId && link.productname) {
          setLinkType("product");
          setProductId(link.productId);
          setProductName(link.productname);
        } else if (link.categoryId && link.categoryname) {
          setLinkType("category");
          setCategoryId(link.categoryId);
          setCategoryName(link.categoryname);
        } else if (link.link) {
          setLinkType("custom");
          setCustomLink(link.link);
        }
      }

      // Auto-fill group selection
      if (banner.groupId) {
        setGroupId(banner.groupId);
      }
    }
  }, [banner, mode]);

  const validateForm = () => {
    const newErrors = {
      image: "",
      productId: "",
      productName: "",
      categoryId: "",
      categoryName: "",
      customLink: "",
      groupId: "",
    };

    // Image validation - in edit mode, allow existing image
    if (!selectedFile && !previewUrl) {
      newErrors.image = "Banner image is required";
    }

    // Link validation based on type
    if (linkType === "product") {
      if (!productId.trim()) {
        newErrors.productId = "Product is required";
      }
      // Remove productName validation since it's automatically set by dropdown
    } else if (linkType === "category") {
      if (!categoryId.trim()) {
        newErrors.categoryId = "Category is required";
      }
      if (!categoryName.trim()) {
        newErrors.categoryName = "Category is required";
      }
    } else if (linkType === "custom") {
      if (!customLink.trim()) {
        newErrors.customLink = "Custom link is required";
      } else if (
        !customLink.startsWith("http://") &&
        !customLink.startsWith("https://")
      ) {
        newErrors.customLink =
          "Please enter a valid URL starting with http:// or https://";
      }
    }

    // Group validation
    if (!groupId.trim()) {
      newErrors.groupId = "Group is required";
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some((error) => error !== "");
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const supportedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
      ];
      if (!supportedTypes.includes(file.type)) {
        toast.error("Please select a valid image file (JPEG, PNG, GIF)");
        event.target.value = "";
        return;
      }

      // Validate file size (2MB max)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        toast.error("File size must be less than 2MB");
        event.target.value = "";
        return;
      }

      setSelectedFile(file);
      setErrors((prev) => ({ ...prev, image: "" }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
    // Always reset input so same file can be selected again
    event.target.value = "";
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setErrors((prev) => ({ ...prev, image: "Banner image is required" }));
  };

  const uploadMedia = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append("fileKey", file, file.name);

    try {
      setMediaLoading(true);
      const response = await categoryAPI.uploadCategoryMedia(formData);
      return response.data.result.files.fileKey[0].name; // Return the uploaded file name
    } catch (error) {
      throw new Error("Failed to upload image");
    } finally {
      setMediaLoading(false);
    }
  };

  const handleCategoryChange = (category: Category | null) => {
    if (category) {
      setCategoryId(category.id);
      setCategoryName(category.name);
      setErrors((prev) => ({ ...prev, categoryId: "", categoryName: "" }));
    } else {
      setCategoryId("");
      setCategoryName("");
    }
  };

  const handleProductChange = (product: Product | null) => {
    if (product) {
      setProductId(product.id);
      setProductName(product.name);
      setErrors((prev) => ({ ...prev, productId: "" }));
    } else {
      setProductId("");
      setProductName("");
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return; // Just return without showing toast
    }

    // If editing and user removed the image, require a new image
    if (mode === "edit" && !selectedFile && !previewUrl) {
      toast.error("Banner image is required. Please upload a new image.");
      setLoading(false);
      return;
    }

    // setSaving(true);
    setLoading(true);
    try {
      let finalImageName = "";

      // Step 1: Handle image upload/change
      if (selectedFile) {
        // New image selected - upload it
        try {
          finalImageName = await uploadMedia(selectedFile);
        } catch (error) {
          toast.error("Failed to upload image. Please try again.");
          setLoading(false);
          // setSaving(false);
          return;
        }
      } else if (banner && banner.media && banner.media.length > 0 && previewUrl) {
        // No new image selected - use existing image name only if previewUrl is present
        finalImageName = banner.media[0].image;
      } else {
        // No image available
        toast.error("Please select an image for the banner");
        setLoading(false);
        // setSaving(false);
        return;
      }

      // Step 2: Prepare banner data based on link type
      let linkData: any = {};

      if (linkType === "product") {
        linkData = {
          productId: productId.trim(),
          productname: productName.trim(),
        };
      } else if (linkType === "category") {
        linkData = {
          categoryId: categoryId.trim(),
          categoryname: categoryName.trim(),
        };
      } else if (linkType === "custom") {
        linkData = {
          link: customLink.trim(),
        };
      }

      // Add groupId to bannerData
      const bannerData = {
        groupId: groupId.trim(),
        media: [
          {
            image: finalImageName,
            video: null,
          },
        ],
        link: [linkData],
      };

      // Step 3: Call add/update banner API
      if (mode === "add") {
        await bannerAPI.addBanner(bannerData);
        toast.success("Banner added successfully!");
      } else if (mode === "edit" && banner) {
        // For edit mode, always call update API
        await bannerAPI.updateBanner(banner.id.toString(), bannerData);
        toast.success("Banner updated successfully!");
      }

      // Reset all form and state fields after successful add/update
      setSelectedFile(null);
      setPreviewUrl("");
      setLinkType("product");
      setProductId("");
      setProductName("");
      setCategoryId("");
      setCategoryName("");
      setCustomLink("");
      setGroupId("");
      setErrors({
        image: "",
        productId: "",
        categoryId: "",
        categoryName: "",
        customLink: "",
        groupId: "",
      });
      onSave(bannerData);
      onClose();
    } catch (error: any) {
      // Handle specific error response structure
      let errorMessage = "Failed to save banner.";

      if (error.response?.data?.detail?.responseData?.message) {
        // Extract the specific error message from the API response
        errorMessage = error.response.data.detail.responseData.message;
      } else if (error.response?.data?.detail?.responseMessage) {
        // Fallback to general response message
        errorMessage = error.response.data.detail.responseMessage;
      } else if (error.message) {
        // Fallback to error message
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
      // setSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl("");
    setLinkType("product");
    setProductId("");
    setCategoryId("");
    setCategoryName("");
    setCustomLink("");
    setErrors({
      image: "",
      productId: "",
      categoryId: "",
      categoryName: "",
      customLink: "",
    });
    onClose();
  };

  const getBannerLink = (banner: Banner) => {
    if (banner.link && banner.link.length > 0) {
      const link = banner.link[0];
      if (link.categoryname) {
        return `Category: ${link.categoryname}`;
      } else if (link.productname) {
        return `Product: ${link.productname}`;
      } else if (link.link) {
        return link.link;
      }
    }
    return "No link";
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Prevent closing on backdrop click or Escape key
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        {mode === "add"
          ? "Add Banner"
          : mode === "edit"
          ? "Edit Banner"
          : "View Banner"}
      </DialogTitle>
      <form>
        <DialogContent sx={{ position: 'relative' }}>
          {/* Loader overlay for saving */}
          {loading && (
            <Box sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(255,255,255,0.7)',
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <CircularProgress color="primary" />
            </Box>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3, opacity: loading ? 0.5 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            {/* Banner Info for View Mode */}
            {banner && mode === "view" && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Banner Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>ID:</strong> {banner.id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Link:</strong> {getBannerLink(banner)}
                </Typography>
              </Box>
            )}

            {/* Link Configuration for Add/Edit Mode */}
            {mode !== "view" && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Link Configuration <span style={{ color: "red" }}>*</span>
                </Typography>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>
                    Link Type <span style={{ color: "red" }}>*</span>
                  </InputLabel>
                  <Select
                    value={linkType}
                    onChange={(e) =>
                      setLinkType(
                        e.target.value as "product" | "category" | "custom"
                      )
                    }
                    label="Link Type *"
                  >
                    <MenuItem value="product">Product</MenuItem>
                    <MenuItem value="category">Category</MenuItem>
                    <MenuItem value="custom">Custom Link</MenuItem>
                  </Select>
                </FormControl>

                {linkType === "product" && (
                  <Autocomplete
                    options={activeProducts}
                    getOptionLabel={(option) => option.name}
                    value={
                      activeProducts.find((prod) => prod.id === productId) || null
                    }
                    onChange={(event, newValue) => handleProductChange(newValue)}
                    onInputChange={(event, newInputValue) => {}}
                    loading={productLoading}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter((option) =>
                        option.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Product"
                        error={!!errors.productId}
                        helperText={errors.productId}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {productLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    )}
                    noOptionsText="No products found"
                  />
                )}

                {linkType === "category" && (
                  <Autocomplete
                    options={activeCategories}
                    getOptionLabel={(option) => option.name}
                    value={
                      activeCategories.find((cat) => cat.id === categoryId) ||
                      null
                    }
                    onChange={(event, newValue) => handleCategoryChange(newValue)}
                    onInputChange={(event, newInputValue) => {}}
                    loading={categoryLoading}
                    filterOptions={(options, { inputValue }) => {
                      return options.filter((option) =>
                        option.name
                          .toLowerCase()
                          .includes(inputValue.toLowerCase())
                      );
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Select Category"
                        error={!!errors.categoryId}
                        helperText={errors.categoryId}
                        required
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {categoryLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <Box component="li" {...props}>
                        <Typography variant="body2">{option.name}</Typography>
                      </Box>
                    )}
                    noOptionsText="No categories found"
                  />
                )}

                {linkType === "custom" && (
                  <TextField
                    fullWidth
                    label="Custom Link"
                    value={customLink}
                    onChange={(e) => {
                      setCustomLink(e.target.value);
                      setErrors((prev) => ({ ...prev, customLink: "" }));
                    }}
                    placeholder="https://example.com"
                    error={!!errors.customLink}
                    helperText={errors.customLink}
                    required
                  />
                )}
              </Box>
            )}

            {/* Group Dropdown */}
            {mode !== "view" && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>
                  Group <span style={{ color: "red" }}>*</span>
                </InputLabel>
                <Select
                  value={groupId}
                  onChange={(e) => setGroupId(e.target.value)}
                  label="Group *"
                  required
                  disabled={groupLoading}
                >
                  {activeGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.groupId && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {errors.groupId}
                  </Typography>
                )}
              </FormControl>
            )}

            {/* Image Upload */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Banner Image <span style={{ color: "red" }}>*</span>
              </Typography>

              <Typography variant="body2" color="info.main" sx={{ mb: 1 }}>
                Aspect Ratio: <strong>649:150</strong> (or <strong>4.33:1</strong>)
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                You can upload image files up to 2MB (JPEG, PNG, GIF).
              </Typography>

              {/* Image Preview */}
              {previewUrl && (
                <Box
                  sx={{ mb: 2, position: "relative", display: "inline-block" }}
                >
                  <Avatar
                    src={previewUrl}
                    alt="Banner"
                    sx={{ width: 200, height: 100, mb: 1 }}
                    variant="rounded"
                  />
                  {(mode === "add" || mode === "edit") && (
                    <IconButton
                      onClick={removeImage}
                      sx={{
                        position: "absolute",
                        top: -8,
                        right: -8,
                        bgcolor: "error.main",
                        color: "white",
                        "&:hover": { bgcolor: "error.dark" },
                      }}
                      size="small"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}

              {/* Upload Button - Show in both add and edit modes */}
              {(mode === "add" || mode === "edit") && (
                <Button
                  component="label"
                  variant="outlined"
                  startIcon={
                    mediaLoading ? <CircularProgress size={20} /> : <UploadIcon />
                  }
                  sx={{ mt: 1 }}
                  disabled={mediaLoading || loading}
                >
                  {mediaLoading
                    ? "Uploading..."
                    : mode === "edit"
                    ? "Change Image"
                    : "Upload Image"}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleFileSelect}
                    disabled={mediaLoading || loading}
                  />
                </Button>
              )}

              {/* Image Error */}
              {errors.image && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors.image}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          {mode !== "view" && (
            <Button onClick={handleSubmit} variant="contained" disabled={loading}>
              {loading ? "Saving..." : mode === "add" ? "Add" : "Update"}
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default BannerDialog;
